import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import {
  AdminPatrolSheetMaterial,
  UpsertAdminPatrolSheetMaterialBody,
} from "../../../../../http/patrol/types/patrolSheetMaterials";
import { usePatrolSheetMaterialsApi } from "../../../../../http/patrol/usePatrolSheetMaterials";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface SheetMaterialFormBody {
  material: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  minValue: number | null;
}

interface SheetMaterialFormProps {
  isDisabled?: boolean;
  defaultValues?: AdminPatrolSheetMaterial;
  onClose: () => void;
}

const SheetMaterialForm: FC<SheetMaterialFormProps> = ({ isDisabled, defaultValues, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const patrolSheetMaterialApi = usePatrolSheetMaterialsApi();

  const onSubmit = () => async (formData: SheetMaterialFormBody) => {
    const body: UpsertAdminPatrolSheetMaterialBody = {
      material: formData.material!,
      order: formData.order!,
      visibility: formData.visibility,
      minValue: formData.minValue!,
    };

    setIsSubmitting(true);

    if (defaultValues?.id) {
      try {
        await patrolSheetMaterialApi.updateAdminPatrolSheetMaterial(defaultValues?.id, body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await patrolSheetMaterialApi.insertAdminPatrolSheetMaterial(body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<SheetMaterialFormBody>
      defaultValues={{
        material: null,
        minValue: null,
        order: 50,
        visibility: "hidden",
      }}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset }) => {
        useEffect(() => {
          if (defaultValues) {
            reset({ ...defaultValues, visibility: defaultValues.visibility });
          }
        }, [defaultValues]);

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.materials.singular} error={errors.material}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isSubmitting}
                  {...register("material", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="minValue"
              control={control}
              rules={{ required: true, min: 0, max: 99 }}
              disabled={isDisabled || isSubmitting}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minValue} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      allowNegative={false}
                      decimalScale={2}
                      suffix=" mm"
                      onBlur={(value) => setValue("minValue", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              disabled={isDisabled || isSubmitting}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SheetMaterialForm;
